import React, { useEffect, useRef, useState } from "react";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";

const siteMedSrc = process.env.REACT_APP_MEDIA_SRC;
const buttonColor = process.env.REACT_APP_BUTTON_COLOR;
const buttonText = process.env.REACT_APP_BUTTON_TEXT;

GlobalWorkerOptions.workerSrc = siteMedSrc + "pdf.worker.min.js";

const PdfViewer = ({ pdfUrl }) => {
  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  const [isLoading, setIsLoading] = useState(false);
  const [jumpPage, setJumpPage] = useState("");

  useEffect(() => {
    const loadPDF = async () => {
      try {
        setIsLoading(true);
        const loadingTask = getDocument(pdfUrl);
        const pdf = await loadingTask.promise;

        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);

        await renderPage(pdf, pageNumber, scale);
      } catch (error) {
        console.error("Error loading PDF:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPDF();
  }, [pdfUrl]);

  const renderPage = async (pdf, pageNum, zoomScale = scale) => {
    try {
      setIsLoading(true);
      const page = await pdf.getPage(pageNum);

      const viewport = page.getViewport({ scale: zoomScale });

      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;
    } catch (error) {
      console.error("Error rendering page:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const goToPrevPage = () => {
    if (pageNumber <= 1) return;
    const newPage = pageNumber - 1;
    setPageNumber(newPage);
    renderPage(pdfDoc, newPage, scale);
  };

  const goToNextPage = () => {
    if (pageNumber >= totalPages) return;
    const newPage = pageNumber + 1;
    setPageNumber(newPage);
    renderPage(pdfDoc, newPage, scale);
  };

  const zoomIn = () => {
    const newScale = scale + 0.25;
    setScale(newScale);
    renderPage(pdfDoc, pageNumber, newScale);
  };

  const zoomOut = () => {
    if (scale <= 0.5) return;
    const newScale = scale - 0.25;
    setScale(newScale);
    renderPage(pdfDoc, pageNumber, newScale);
  };

  const handleJumpPageChange = (e) => {
    setJumpPage(e.target.value);
  };

  const handleJumpPageSubmit = (e) => {
    e.preventDefault();
    const targetPage = parseInt(jumpPage);
    if (!targetPage || targetPage < 1 || targetPage > totalPages) {
      alert(`Please enter a valid page number between 1 and ${totalPages}`);
      return;
    }
    setPageNumber(targetPage);
    renderPage(pdfDoc, targetPage, scale);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          overflowY: "scroll",
          width: "100%",
          marginTop: "2rem",
          position: "relative",
        }}
      >
        {isLoading && (
          <div
            style={{
              position: "absolute",
              zIndex: 1,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              fontSize: "1.5rem",
              fontWeight: "bold",
            }}
          >
            Loading...
          </div>
        )}
        <canvas
          ref={canvasRef}
          style={{
            border: "1px solid #ccc",
            opacity: isLoading ? 0.5 : 1,
            transition: "opacity 0.3s ease",
          }}
        />
      </div>

      {/* Navigation Buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "10px",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <button
          className="btn-red"
          style={{
            backgroundColor: buttonColor,
            color: buttonText,
            display: "inline-block",
            padding: "10px 20px",
            borderRadius: "5px",
            textDecoration: "none",
            textAlign: "center",
            width: "130px",
            cursor: "pointer",
          }}
          onClick={goToPrevPage}
          disabled={pageNumber <= 1}
        >
          Previous
        </button>

        <p style={{ margin: "1rem" }}>
          Page {pageNumber} of {totalPages}
        </p>

        <button
          className="btn-red"
          style={{
            backgroundColor: buttonColor,
            color: buttonText,
            display: "inline-block",
            padding: "10px 20px",
            borderRadius: "5px",
            textDecoration: "none",
            textAlign: "center",
            width: "130px",
            cursor: "pointer",
          }}
          onClick={goToNextPage}
          disabled={pageNumber >= totalPages}
        >
          Next
        </button>
      </div>

      {/* Zoom Controls */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "10px",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <button
          style={{
            backgroundColor: buttonColor,
            color: buttonText,
            padding: "10px 20px",
            borderRadius: "5px",
            cursor: "pointer",
            width: "130px",
          }}
          onClick={zoomOut}
          disabled={scale <= 0.5}
        >
          Zoom Out
        </button>

        <p style={{ margin: "1rem" }}>Zoom: {scale.toFixed(2)}x</p>

        <button
          style={{
            backgroundColor: buttonColor,
            color: buttonText,
            padding: "10px 20px",
            borderRadius: "5px",
            cursor: "pointer",
            width: "130px",
          }}
          onClick={zoomIn}
        >
          Zoom In
        </button>
      </div>

      {/* Jump to Page */}
      <form
        onSubmit={handleJumpPageSubmit}
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "10px",
          gap: "0.5rem",
        }}
      >
        <input
          type="number"
          min="1"
          max={totalPages}
          placeholder={`Page (1 - ${totalPages})`}
          value={jumpPage}
          onChange={handleJumpPageChange}
          style={{
            padding: "10px",
            width: "150px",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        />
        <button
          type="submit"
          style={{
            backgroundColor: buttonColor,
            color: buttonText,
            padding: "10px 20px",
            borderRadius: "5px",
            cursor: "pointer",
            width: "130px",
          }}
        >
          Go
        </button>
      </form>
    </div>
  );
};

export default PdfViewer;
